import * as React from 'react';
import { toast } from 'react-toastify';
import { ToastComponent, ToastType, ToastOptions } from '@common/components';

import { actions } from 'react-redux-form';
import { Action, Dispatch } from 'redux';
import { AppState } from '..';
import { customerService } from '@services/CustomerService';
import { CustomerViewModel, CustomerContractModel } from '@models/Customer';
import { PageViewModel, blankOptionsVM, AddressViewModel, AppDispatcher, blankAddressVM } from '@models/common.models';
import { contractService } from '@services/ContractService';
import { ContractSimpleViewModel } from '@models/Contract';
import { documentService } from '@services/DocumentService';
import { DocumentViewModel, FileViewModel } from '@models/Document';
import { getById, setFileListLoading } from '@common/components/common.actions';
import FormNames from '@constants/FormNames.enum';
// We can use enums instead of type constants like in the example. This is a newer TS feature
export enum CustomerActionType {
  /********** Save **********/
  SaveCustomerStart = 'CustomerSaveStart',
  SaveCustomerSuccess = 'CustomerSaveSuccess',
  SaveCustomerFailure = 'CustomerSaveFailure',

  /********** Get **********/
  GetCustomerStart = 'CustomerGetStart',
  GetCustomerSuccess = 'CustomerGetSuccess',
  GetCustomerFailure = 'CustomerGetFailure',

  /********** Clear **********/
  ClearCustomer = 'ClearCustomer',

  /********** SetCustomerAddresses **********/
  SetCustomerAddresses = 'SetCustomerAddresses',

  /********** Search **********/
  SearchCustomersInit = 'SearchCustomersInit',
  SearchCustomersSuccess = 'SearchCustomersSuccess',
  SearchCustomersFailure = 'SearchCustomersFailure',

  LoadCustomer = 'LoadCustomer',

  /********** SearchContractsByCustomer **********/
  SearchContractsByCustomerInit = 'SearchContractsByCustomerInit',
  SearchContractsByCustomerSuccess = 'SearchContractsByCustomerSuccess',
  SearchContractsByCustomerFailure = 'SearchContractsByCustomerFailure',

  /********** SearchRelatedContractsByCustomer **********/
  SearchRelatedContractsByCustomerInit = 'SearchRelatedContractsByCustomerInit',
  SearchRelatedContractsByCustomerSuccess = 'SearchRelatedContractsByCustomerSuccess',
  SearchRelatedContractsByCustomerFailure = 'SearchRelatedContractsByCustomerFailure',

  /********** SaveCustomerFiles **********/
  SaveCustomerFilesStart = 'SaveCustomerFilesStart',
  SaveCustomerFilesSuccess = 'SaveCustomerFilesSuccess',
  SaveCustomerFilesFailure = 'SaveCustomerFilesFailure',

  /********** RemoveCustomerFile **********/
  RemoveCustomerFileStart = 'RemoveCustomerFileStart',
  RemoveCustomerFileSuccess = 'RemoveCustomerFileSuccess',
  RemoveCustomerFileFailure = 'RemoveCustomerFileFailure',

  LoadCustomerIntoDetails = 'LoadCustomerIntoDetails',

  /********** ResetPassword **********/
  ResetPasswordStart = 'ResetPasswordStart',
  ResetPasswordSuccess = 'ResetPasswordSuccess',
  ResetPasswordFailure = 'ResetPasswordFailure',
  /********** RelateContractToCustomer **********/
  RelateContractToCustomerSuccess = 'RelateContractToCustomerSuccess',
  RelateContractToCustomerFailure = 'RelateContractToCustomerFailure',

  /********** UnrelateContractToCustomer **********/
  UnrelateContractToCustomerSuccess = 'UnrelateContractToCustomerSuccess',
  UnrelateContractToCustomerFailure = 'UnrelateContractToCustomerFailure'
}

// This is defining the action definition for certain actions on the page.
// There may be multiple types of these on a given page.
// All properties should be readonly.

/********** Save **********/
export interface SaveCustomerStartAction extends Action {
  readonly type: CustomerActionType.SaveCustomerStart;
}
export interface SaveCustomerSuccessAction extends Action {
  readonly type: CustomerActionType.SaveCustomerSuccess;
  readonly currentId: number;
}
export interface SaveCustomerFailureAction extends Action {
  readonly type: CustomerActionType.SaveCustomerFailure;
  readonly errorMessage: string;
}

/********** Get **********/
export interface GetCustomerStartAction extends Action {
  readonly type: CustomerActionType.GetCustomerStart;
}
export interface GetCustomerSuccessAction extends Action {
  readonly type: CustomerActionType.GetCustomerSuccess;
  readonly customer: CustomerViewModel;
}
export interface GetCustomerFailureAction extends Action {
  readonly type: CustomerActionType.GetCustomerFailure;
  readonly errorMessage: string;
}

/********** Clear **********/
export interface ClearCustomerAction extends Action {
  readonly type: CustomerActionType.ClearCustomer;
}

/********** SetCustomerAddresses **********/
export interface SetCustomerAddressesAction extends Action {
  readonly type: CustomerActionType.SetCustomerAddresses;
  readonly addresses: Array<AddressViewModel>;
}

/********** Search **********/
export interface SearchCustomersInitAction extends Action {
  readonly type: CustomerActionType.SearchCustomersInit;
  readonly query: string | null;
}

export interface SearchCustomersSuccessAction extends Action {
  readonly type: CustomerActionType.SearchCustomersSuccess;
  readonly pageNumber: number;
  readonly pageSize: number;
  readonly searchResult: PageViewModel<CustomerViewModel>;
}

export interface SearchCustomersFailureAction extends Action {
  readonly type: CustomerActionType.SearchCustomersFailure;
  readonly errorMessage: string;
}

/********** SearchContractsByCustomer **********/
export interface SearchContractsByCustomerInitAction extends Action {
  readonly type: CustomerActionType.SearchContractsByCustomerInit;
  readonly customerId: number;
}
export interface SearchContractsByCustomerSuccessAction extends Action {
  readonly type: CustomerActionType.SearchContractsByCustomerSuccess;
  readonly pageNumber: number;
  readonly pageSize: number;
  readonly searchResult: PageViewModel<ContractSimpleViewModel>;
}
export interface SearchContractsByCustomerFailureAction extends Action {
  readonly type: CustomerActionType.SearchContractsByCustomerFailure;
  readonly errorMessage: string;
}

/********** SearchRelatedContractsByCustomer **********/
export interface SearchRelatedContractsByCustomerInitAction extends Action {
  readonly type: CustomerActionType.SearchRelatedContractsByCustomerInit;
  readonly customerId: number;
}
export interface SearchRelatedContractsByCustomerSuccessAction extends Action {
  readonly type: CustomerActionType.SearchRelatedContractsByCustomerSuccess;
  readonly pageNumber: number;
  readonly pageSize: number;
  readonly searchResult: PageViewModel<ContractSimpleViewModel>;
}
export interface SearchRelatedContractsByCustomerFailureAction extends Action {
  readonly type: CustomerActionType.SearchRelatedContractsByCustomerFailure;
  readonly errorMessage: string;
}

/********** SaveCustomerFiles **********/
export interface SaveCustomerFilesStartAction extends Action {
  readonly type: CustomerActionType.SaveCustomerFilesStart;
}
export interface SaveCustomerFilesSuccessAction extends Action {
  readonly type: CustomerActionType.SaveCustomerFilesSuccess;
  readonly customerId: number;
  readonly documents: DocumentViewModel[];
}
export interface SaveCustomerFilesFailureAction extends Action {
  readonly type: CustomerActionType.SaveCustomerFilesFailure;
  readonly errorMessage: string;
}

/********** RemoveCustomerFile **********/
export interface RemoveCustomerFileStartAction extends Action {
  readonly type: CustomerActionType.RemoveCustomerFileStart;
}
export interface RemoveCustomerFileSuccessAction extends Action {
  readonly type: CustomerActionType.RemoveCustomerFileSuccess;
  readonly customerId: number;
  readonly fileId: number;
}
export interface RemoveCustomerFileFailureAction extends Action {
  readonly type: CustomerActionType.RemoveCustomerFileFailure;
  readonly errorMessage: string;
}

export interface LoadCustomerIntoDetailsAction extends Action {
  readonly type: CustomerActionType.LoadCustomerIntoDetails;
  readonly customer: CustomerViewModel;
}

export interface ResetPasswordStartAction extends Action {
  readonly type: CustomerActionType.ResetPasswordStart;
}

export interface ResetPasswordSuccessAction extends Action {
  readonly type: CustomerActionType.ResetPasswordSuccess;
}

export interface ResetPasswordFailureAction extends Action {
  readonly type: CustomerActionType.ResetPasswordFailure;
  readonly errorMessage: string;
}
/********** RelateContractToCustomer **********/
export interface RelateContractToCustomerSuccessAction extends Action {
  readonly type: CustomerActionType.RelateContractToCustomerSuccess;
  readonly customerContract: CustomerContractModel;
}

export interface RelateContractToCustomerFailureAction extends Action {
  readonly type: CustomerActionType.RelateContractToCustomerFailure;
  readonly errorMessage: string;
}

/********** UnrelateContractToCustomer **********/
export interface UnrelateContractToCustomerSuccessAction extends Action {
  readonly type: CustomerActionType.UnrelateContractToCustomerSuccess;
  readonly customerContract: CustomerContractModel;
}

export interface UnrelateContractToCustomerFailureAction extends Action {
  readonly type: CustomerActionType.UnrelateContractToCustomerFailure;
  readonly errorMessage: string;
}

// Actions for each action type declared here.
// They should all return an action object.

/********** Save **********/
export const saveCustomer = (model: CustomerViewModel, saveCallback?: Function) => {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(saveCustomerStart());
    try {
      let customerVM = { ...model };
      if (!customerVM.phone2Type || customerVM.phone2Type.id < 0) {
        customerVM.phone2Type = blankOptionsVM;
      }
      if (!customerVM.phone1Type || customerVM.phone1Type.id < 0) {
        customerVM.phone1Type = blankOptionsVM;
      }

      if (customerVM.addresses && !Array.isArray(customerVM.addresses)) {
        customerVM.addresses = Object.keys(customerVM.addresses).map(key => customerVM.addresses[key]);
      }

      const id = await customerService.saveCustomer(customerVM);
      if (saveCallback) {
        saveCallback(id);
      } else {
        if (model.id === 0) {
          dispatch(actions.reset(FormNames.CustomerForm));
        }
      }
      return dispatch(saveCustomerSuccess(id));
    } catch (e) {
      return dispatch(saveCustomerFailure(e.toString()));
    }
  };
};
export function saveCustomerStart(): SaveCustomerStartAction {
  return {
    type: CustomerActionType.SaveCustomerStart
  };
}
export function saveCustomerSuccess(id: number): SaveCustomerSuccessAction {
  toast.success(
    <ToastComponent type={ToastType.Success} message={'Customer Saved Successfully'} />,
    ToastOptions.Success
  );

  return {
    type: CustomerActionType.SaveCustomerSuccess,
    currentId: id
  };
}
export function saveCustomerFailure(errorMessage: string): SaveCustomerFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Network error, unable to save customer. ' + errorMessage} />,
    ToastOptions.Error
  );

  return {
    type: CustomerActionType.SaveCustomerFailure,
    errorMessage: errorMessage
  };
}

export function resetPassword(accountId: number) {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(resetPasswordStart());
    try {
      await customerService.resetPassword(accountId);
      return dispatch(resetPasswordSuccess());
    } catch (e) {
      return dispatch(resetPasswordFailure(e.toString()));
    }
  };
}
export function resetPasswordStart(): ResetPasswordStartAction {
  return {
    type: CustomerActionType.ResetPasswordStart
  };
}
export function resetPasswordSuccess(): ResetPasswordSuccessAction {
  toast.success(
    <ToastComponent type={ToastType.Success} message={'Password reset email was sent successfully'} />,
    ToastOptions.Success
  );

  return {
    type: CustomerActionType.ResetPasswordSuccess
  };
}
export function resetPasswordFailure(errorMessage: string): ResetPasswordFailureAction {
  toast.error(
    <ToastComponent
      type={ToastType.Error}
      message={'Network error, unable to send password reset email. ' + errorMessage}
    />,
    ToastOptions.Error
  );

  return {
    type: CustomerActionType.ResetPasswordFailure,
    errorMessage: errorMessage
  };
}

export function getCustomer(id: number, accountId?: number) {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(getCustomerStart());
    try {
      let customer: CustomerViewModel;
      if (accountId !== undefined) {
        customer = await customerService.getCustomerByAccountId(accountId);
      } else {
        customer = await customerService.getCustomer(id);
      }

      return dispatch(getCustomerSuccess(customer));
    } catch (e) {
      return dispatch(getCustomerFailure(e.toString()));
    }
  };
}

export function getCustomerStart(): GetCustomerStartAction {
  return {
    type: CustomerActionType.GetCustomerStart
  };
}

export function getCustomerSuccess(customer: CustomerViewModel): GetCustomerSuccessAction {
  return {
    type: CustomerActionType.GetCustomerSuccess,
    customer: customer
  };
}

export function getCustomerFailure(errorMessage: string): GetCustomerFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Network error, unable to get customer detail ' + errorMessage} />,
    ToastOptions.Error
  );

  return {
    type: CustomerActionType.GetCustomerFailure,
    errorMessage: errorMessage
  };
}

/********** SetCustomerAddresses **********/
export interface SetCustomerAddressesAction extends Action {
  readonly type: CustomerActionType.SetCustomerAddresses;
  readonly addresses: Array<AddressViewModel>;
}
export function setCustomerAddresses(customerAddresses: Array<AddressViewModel>): SetCustomerAddressesAction {
  return {
    type: CustomerActionType.SetCustomerAddresses,
    addresses: customerAddresses
  };
}

export function addCustomerAddress(address?: AddressViewModel) {
  return async (dispatch: AppDispatcher, getState: () => AppState) => {
    var customerForm = getState().customerForm;
    if (!customerForm) {
      toast.error(
        <ToastComponent type={ToastType.Error} message={'Unable to add address. Error: No customer form.'} />,
        ToastOptions.Error
      );
      return;
    }
    const newAddress = address ? address : blankAddressVM;
    var addresses = customerForm.addresses ? customerForm.addresses.slice(0) : [];
    addresses.push(newAddress);
    dispatch(setCustomerAddresses(addresses));
    dispatch(actions.load(FormNames.CustomerForm + `.addresses[${addresses.length - 1}]`, newAddress));
    const hasPrimary = addresses.some((value: AddressViewModel, index: number, array: AddressViewModel[]) => {
      return !!value.primary;
    });
    if (!hasPrimary && addresses.length === 1) {
      dispatch(actions.change(FormNames.CustomerForm + '.addresses[0].primary', true));
    }
  };
}

export function removeCustomerAddress(indexToRemove: number) {
  return async (dispatch: AppDispatcher, getState: () => AppState) => {
    if (indexToRemove < 0) {
      toast.error(
        <ToastComponent type={ToastType.Error} message={'Unable to remove address. Error: Invalid index.'} />,
        ToastOptions.Error
      );
      return;
    }
    var customerForm = getState().customerForm;
    if (!customerForm) {
      toast.error(
        <ToastComponent type={ToastType.Error} message={'Unable to remove address. Error: No customer form.'} />,
        ToastOptions.Error
      );
      return;
    }
    if (!customerForm.addresses) {
      toast.error(
        <ToastComponent type={ToastType.Error} message={'Unable to remove address. Error: No customer addresses.'} />,
        ToastOptions.Error
      );
      return;
    }
    var addresses = customerForm.addresses.slice(0);
    addresses.splice(indexToRemove, 1);
    await dispatch(setCustomerAddresses(addresses));
    await dispatch(actions.change(FormNames.CustomerForm + '.addresses', addresses));
  };
}

/********** Search **********/
export const searchCustomers = (
  pageNumber: number,
  pageSize: number,
  query: string | null,
  orderBy: string,
  orderDirection: string
) => {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(searchCustomersInit(query));
    try {
      let searchResult: PageViewModel<CustomerViewModel> = await customerService.searchCustomers(
        query,
        undefined,
        pageNumber,
        pageSize,
        orderBy,
        orderDirection
      );
      dispatch(searchCustomersSuccess(pageNumber, pageSize, searchResult));
    } catch (e) {
      dispatch(searchCustomersFailure(e.toString()));
    }
  };
};

export function searchCustomersInit(query: string | null): SearchCustomersInitAction {
  return {
    query: query,
    type: CustomerActionType.SearchCustomersInit
  };
}

export function searchCustomersFailure(errorMessage: string): SearchCustomersFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Network error, unable to search customers. ' + errorMessage} />,
    ToastOptions.Error
  );

  return {
    errorMessage: errorMessage,
    type: CustomerActionType.SearchCustomersFailure
  };
}

export function searchCustomersSuccess(
  pageNumber: number,
  pageSize: number,
  result: PageViewModel<CustomerViewModel>
): SearchCustomersSuccessAction {
  return {
    pageNumber: pageNumber,
    pageSize: pageSize,
    searchResult: result,
    type: CustomerActionType.SearchCustomersSuccess
  };
}

/********** SearchContractsByCustomer **********/
export const searchContractsByCustomer = (customerId: number, pageNumber: number, pageSize: number) => {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(searchContractsByCustomerInit(customerId));
    try {
      let searchResult: PageViewModel<ContractSimpleViewModel> = await contractService.searchByCustomer(
        customerId,
        pageNumber,
        pageSize
      );
      dispatch(searchContractsByCustomerSuccess(pageNumber, pageSize, searchResult));
    } catch (e) {
      dispatch(searchContractsByCustomerFailure(e.toString()));
    }
  };
};

export function searchContractsByCustomerInit(customerId: number): SearchContractsByCustomerInitAction {
  return {
    customerId: customerId,
    type: CustomerActionType.SearchContractsByCustomerInit
  };
}

export function searchContractsByCustomerFailure(errorMessage: string): SearchContractsByCustomerFailureAction {
  toast.error(
    <ToastComponent
      type={ToastType.Error}
      message={'Network error, unable to search contracts by customer. ' + errorMessage}
    />,
    ToastOptions.Error
  );

  return {
    errorMessage: errorMessage,
    type: CustomerActionType.SearchContractsByCustomerFailure
  };
}

export function searchContractsByCustomerSuccess(
  pageNumber: number,
  pageSize: number,
  result: PageViewModel<ContractSimpleViewModel>
): SearchContractsByCustomerSuccessAction {
  return {
    pageNumber: pageNumber,
    pageSize: pageSize,
    searchResult: result,
    type: CustomerActionType.SearchContractsByCustomerSuccess
  };
}

/********** SearchRelatedContractsByCustomer **********/
export const searchRelatedContractsByCustomer = (customerId: number, pageNumber: number, pageSize: number) => {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(searchRelatedContractsByCustomerInit(customerId));
    try {
      let searchResult: PageViewModel<ContractSimpleViewModel> = await contractService.searchRelatedByCustomer(
        customerId,
        pageNumber,
        pageSize
      );
      dispatch(searchRelatedContractsByCustomerSuccess(pageNumber, pageSize, searchResult));
    } catch (e) {
      dispatch(searchRelatedContractsByCustomerFailure(e.toString()));
    }
  };
};

export function searchRelatedContractsByCustomerInit(customerId: number): SearchRelatedContractsByCustomerInitAction {
  return {
    customerId: customerId,
    type: CustomerActionType.SearchRelatedContractsByCustomerInit
  };
}

export function searchRelatedContractsByCustomerFailure(
  errorMessage: string
): SearchRelatedContractsByCustomerFailureAction {
  toast.error(
    <ToastComponent
      type={ToastType.Error}
      message={'Network error, unable to search related contracts by customer. ' + errorMessage}
    />,
    ToastOptions.Error
  );

  return {
    errorMessage: errorMessage,
    type: CustomerActionType.SearchRelatedContractsByCustomerFailure
  };
}

export function searchRelatedContractsByCustomerSuccess(
  pageNumber: number,
  pageSize: number,
  result: PageViewModel<ContractSimpleViewModel>
): SearchRelatedContractsByCustomerSuccessAction {
  return {
    pageNumber: pageNumber,
    pageSize: pageSize,
    searchResult: result,
    type: CustomerActionType.SearchRelatedContractsByCustomerSuccess
  };
}

/********** ReloadCustomer **********/
export function reloadCustomer(id: number) {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(actions.reset(FormNames.CustomerForm));
    dispatch(getById<CustomerViewModel>(id, getCustomerFromService)).then(customer => {
      if (!customer) {
        return;
      }

      dispatch(getCustomerSuccess(customer));
      dispatch(actions.load(FormNames.CustomerForm, customer));
    });
  };
}

/********** SaveCustomerFiles **********/
export const saveCustomerFiles = (customerId: number, fileVMs: FileViewModel[]) => {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(saveCustomerFilesStart());
    dispatch(setFileListLoading(true));
    try {
      const documentVMs = await documentService.saveFiles(fileVMs, customerId);
      dispatch(saveCustomerFilesSuccess(customerId, documentVMs));
      dispatch(reloadCustomer(customerId));
      dispatch(setFileListLoading(false));
    } catch (e) {
      dispatch(saveCustomerFilesFailure(e as string));
      dispatch(setFileListLoading(false));
    }
  };
};

export function saveCustomerFilesStart(): SaveCustomerFilesStartAction {
  return {
    type: CustomerActionType.SaveCustomerFilesStart
  };
}

export function saveCustomerFilesSuccess(
  customerId: number,
  documents: DocumentViewModel[]
): SaveCustomerFilesSuccessAction {
  return {
    type: CustomerActionType.SaveCustomerFilesSuccess,
    customerId: customerId,
    documents: documents
  };
}

export function saveCustomerFilesFailure(errorMessage: string): SaveCustomerFilesFailureAction {
  return {
    type: CustomerActionType.SaveCustomerFilesFailure,
    errorMessage: errorMessage
  };
}

/********** RemoveCustomerFile **********/
export const removeCustomerFile = (customerId: number, fileId: number) => {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(removeCustomerFileStart());
    dispatch(setFileListLoading(true));
    try {
      await documentService.removeFile(fileId);
      dispatch(removeCustomerFileSuccess(customerId, fileId));
      dispatch(reloadCustomer(customerId));
      dispatch(setFileListLoading(false));
    } catch (e) {
      dispatch(removeCustomerFileFailure(e as string));
      dispatch(setFileListLoading(false));
    }
  };
};

export function removeCustomerFileStart(): RemoveCustomerFileStartAction {
  return {
    type: CustomerActionType.RemoveCustomerFileStart
  };
}
export function removeCustomerFileSuccess(customerId: number, fileId: number): RemoveCustomerFileSuccessAction {
  return {
    type: CustomerActionType.RemoveCustomerFileSuccess,
    customerId: customerId,
    fileId: fileId
  };
}

export function removeCustomerFileFailure(errorMessage: string): RemoveCustomerFileFailureAction {
  return {
    type: CustomerActionType.RemoveCustomerFileFailure,
    errorMessage: errorMessage
  };
}

export function loadCustomerIntoDetails(customer: CustomerViewModel): LoadCustomerIntoDetailsAction {
  return {
    type: CustomerActionType.LoadCustomerIntoDetails,
    customer: customer
  };
}

export function getCustomerFromService(id: number) {
  return customerService.getCustomer(id);
}

export function clearCustomer(): ClearCustomerAction {
  return {
    type: CustomerActionType.ClearCustomer
  };
}

/********** RelateContractToCustomer **********/
export const relateContractToCustomer = (customerContract: CustomerContractModel) => {
  return async (dispatch: Dispatch<AppState>) => {
    try {
      contractService.saveRelatedContract(customerContract);

      dispatch(relateContractToCustomerSuccess(customerContract));
    } catch (e) {
      dispatch(relateContractToCustomerFailure(e.toString()));
    }
  };
};

export function relateContractToCustomerSuccess(
  customerContract: CustomerContractModel
): RelateContractToCustomerSuccessAction {
  return {
    type: CustomerActionType.RelateContractToCustomerSuccess,
    customerContract: customerContract
  };
}

export function relateContractToCustomerFailure(errorMessage: string): RelateContractToCustomerFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Unable to associate contract to customer. ' + errorMessage} />,
    ToastOptions.Error
  );

  return {
    type: CustomerActionType.RelateContractToCustomerFailure,
    errorMessage: errorMessage
  };
}

/********** UnrelateContractToCustomer **********/
export const unrelateContractToCustomer = (customerContract: CustomerContractModel) => {
  return async (dispatch: Dispatch<AppState>) => {
    try {
      contractService.removeRelatedContract(customerContract);

      dispatch(unrelateContractToCustomerSuccess(customerContract));
    } catch (e) {
      dispatch(unrelateContractToCustomerFailure(e.toString()));
    }
  };
};

export function unrelateContractToCustomerSuccess(
  customerContract: CustomerContractModel
): UnrelateContractToCustomerSuccessAction {
  return {
    type: CustomerActionType.UnrelateContractToCustomerSuccess,
    customerContract: customerContract
  };
}

export function unrelateContractToCustomerFailure(errorMessage: string): UnrelateContractToCustomerFailureAction {
  toast.error(
    <ToastComponent
      type={ToastType.Error}
      message={'Unable to remove related contract from customer. ' + errorMessage}
    />,
    ToastOptions.Error
  );

  return {
    type: CustomerActionType.UnrelateContractToCustomerFailure,
    errorMessage: errorMessage
  };
}



// WEBPACK FOOTER //
// ./src/customer/customer.actions.tsx